import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Avatar, Badge, EmptyRow, Field, Loading, Modal, Page, Pager, Tabs } from "../components/ui";
import { money, PAGE_SIZE, qs, setQuery, text, when } from "../format";

export function WalletAdjust({ member, direction, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const credit = direction === "credit";

  async function submit(event) {
    event.preventDefault();
    const next = {};
    const value = Number(amount);
    const reason = note.trim();
    if (!Number.isFinite(value) || value <= 0) next.amount = "Amount must be greater than 0";
    if (reason.length < 3 || reason.length > 300) next.note = "Note must be 3 to 300 characters";
    setErrors(next);
    setError("");
    if (Object.keys(next).length) return;

    setBusy(true);
    try {
      const result = await api("/wallet-adjustments", {
        method: "POST",
        body: { userId: member.id, direction, amount: value, note: reason },
      });
      onDone(result);
    } catch (err) {
      setError(errorText(err) || err.message || "Request failed");
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={credit ? "Credit wallet" : "Debit wallet"} onClose={onClose}>
      <p className="dialog-text">
        {credit ? "Adds to" : "Takes from"} {member.name}'s spendable wallet. Locked balance is not changed.
        {credit ? "" : " A debit larger than the spendable balance is rejected."}
      </p>
      {error ? <div className="alert">{error}</div> : null}
      <form onSubmit={submit}>
        <Field label="Amount" error={errors.amount}>
          <input type="number" min="0" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </Field>
        <Field label="Note" error={errors.note} hint="3 to 300 characters. Shown on the member statement.">
          <textarea rows={4} maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className={credit ? "btn" : "btn danger"} disabled={busy}>
            {busy ? "Saving..." : credit ? "Credit" : "Debit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Members() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "";
  const searchValue = params.get("search") || "";
  const [search, setSearch] = useState(searchValue);
  const list = useFetch("/members" + qs({ page, limit: PAGE_SIZE, search: searchValue, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [adjust, setAdjust] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setSearch(searchValue);
  }, [searchValue]);

  function onSearch(event) {
    event.preventDefault();
    setQuery(params, setParams, { search: search.trim(), page: "" });
  }

  return (
    <Page title="Members" text="Search by name, email, phone or referral code. View opens that member's deposits, withdrawals, packages, transfers and statement. Credit and Debit change the spendable wallet. Locked is a pending withdrawal and is not changed.">
      <Alerts error={list.error} notice={notice} />
      <form className="toolbar" onSubmit={onSearch}>
        <input
          value={search}
          placeholder="Search members"
          onChange={(event) => setSearch(event.target.value)}
        />
        <button className="btn small">Search</button>
        {searchValue ? (
          <button type="button" className="btn ghost small" onClick={() => setQuery(params, setParams, { search: "", page: "" })}>
            Clear
          </button>
        ) : null}
      </form>
      <Tabs
        value={status}
        onChange={(value) => setQuery(params, setParams, { status: value, page: "" })}
        options={[
          { value: "", label: "All" },
          { value: "active", label: "Active" },
          { value: "blocked", label: "Blocked" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Country</th>
                <th>Sponsor</th>
                <th>Code</th>
                <th className="num">Wallet</th>
                <th className="num">Locked</th>
                <th>KYC</th>
                <th>Status</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="name-cell">
                      <Avatar src={row.profileImage} name={row.name} />
                      <Link to={"/admin/members/" + row.id}>{row.name}</Link>
                    </div>
                  </td>
                  <td>{row.email}</td>
                  <td>{text(row.phone)}</td>
                  <td>{text(row.country)}</td>
                  <td>
                    {row.sponsorId ? <Link className="mono" to={"/admin/members/" + row.sponsorId}>{String(row.sponsorId)}</Link> : "—"}
                  </td>
                  <td className="code">{row.referralCode}</td>
                  <td className="num">{money(row.balance)}</td>
                  <td className="num">{money(row.lockedBalance)}</td>
                  <td><Badge value={row.kycStatus} /></td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.createdAt)}</td>
                  <td>
                    <div className="row-actions">
                      <Link className="btn ghost small" to={"/admin/members/" + row.id + "/activity"}>View</Link>
                      <button type="button" className="btn small" onClick={() => { setNotice(""); setAdjust({ member: row, direction: "credit" }); }}>Credit</button>
                      <button type="button" className="btn danger small" onClick={() => { setNotice(""); setAdjust({ member: row, direction: "debit" }); }}>Debit</button>
                    </div>
                  </td>
                </tr>
              )) : <EmptyRow cols={12} text="No members" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager
          page={page}
          limit={PAGE_SIZE}
          total={list.data.total}
          onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })}
        />
      ) : null}
      {adjust ? (
        <WalletAdjust
          member={adjust.member}
          direction={adjust.direction}
          onClose={() => setAdjust(null)}
          onDone={(result) => {
            setAdjust(null);
            setNotice(result.message || "Wallet updated");
            list.reload();
          }}
        />
      ) : null}
    </Page>
  );
}
